/**
 * AI PRICE BOOK — the ONE place a per-unit provider rate lives for services billed by a single
 * meter (audio seconds, characters, requests). Business code never holds a rate: it names a
 * price-book entry by id, and ai_call_log records that id beside the quantity and the unit price,
 * so every row can be re-priced from its own columns if a rate here changes.
 *
 * Token-billed LLMs (Gemini, Claude) keep their two-meter input/output rates in modelCost.ts
 * MODEL_PRICES; this book covers everything that is priced as quantity × one unit rate.
 *
 * Every entry carries its provenance: which official meter it is, in which region, the list price
 * exactly as published and the unit it is published in, and when it was read. Rates are LIST
 * prices (pay-as-you-go). A negotiated discount, a commitment tier or an EA rate is NOT reflected
 * — if the Speech resource is ever moved to a commitment tier, these entries must be updated.
 *
 * TO UPDATE A RATE: re-query the source (see `source.query`), change `listPriceUsd`,
 * `effectiveFrom` and `retrievedOn` together, in the same commit. Nothing else changes.
 */
const RETAIL = "https://prices.azure.com/api/retail/prices?$filter=armRegionName eq 'centralindia' and productName eq 'Azure Speech'";
/**
 * Azure AI Speech, resource `talenthop-speech` (S0 pay-as-you-go, Central India — FOUNDER_PACK §4).
 * The Retail Prices API names the standard pay-as-you-go meters with skuName "S1"; there is no S0
 * meter in it. Verified 23 Sep 2026.
 */
export const AI_PRICE_BOOK = [
    {
        // The live interview's recogniser (browser Speech SDK, continuous recognition, AT-START
        // language identification — continuous LID is never enabled, so the "Enhanced Feature
        // Audio" add-on meter does not apply). Billed on audio SENT, in one-second increments.
        id: "azure_speech:realtime_stt:centralindia:2018-11-01",
        provider: "azure_speech",
        service: "realtime_stt",
        unit: "audio_second",
        listPriceUsd: 1.0,
        listPricePer: 3600,
        publishedUnit: "1 Hour",
        region: "centralindia",
        source: { kind: "azure_retail_prices_api", query: RETAIL, productName: "Azure Speech", skuName: "S1", meterName: "S1 Speech To Text" },
        effectiveFrom: "2018-11-01",
        retrievedOn: "2026-09-23",
    },
    {
        // The worker's verified-transcript re-transcription (speechtotext/transcriptions:transcribe,
        // api-version 2024-11-15, standard model). NOT the batch meter ($0.18/hr) the old estimate
        // used, and NOT the "Fast Transcription Promo" meter ($0.10/hr from 2026-09-01), which is the
        // MAI-Transcribe-2 model — a model we do not select.
        id: "azure_speech:fast_transcription:centralindia:2024-11-01",
        provider: "azure_speech",
        service: "fast_transcription",
        unit: "audio_second",
        listPriceUsd: 0.36,
        listPricePer: 3600,
        publishedUnit: "1 Hour",
        region: "centralindia",
        source: { kind: "azure_retail_prices_api", query: RETAIL, productName: "Azure Speech", skuName: "Fast Transcription", meterName: "Fast Transcription Speech To Text" },
        effectiveFrom: "2024-11-01",
        retrievedOn: "2026-09-23",
    },
    {
        // Asha's voice: standard (non-HD) neural voices (…Neural), billed per CHARACTER of the SSML
        // request — see ttsBillableCharacters for exactly what counts.
        id: "azure_speech:neural_tts:centralindia:2024-02-01",
        provider: "azure_speech",
        service: "neural_tts",
        unit: "character",
        listPriceUsd: 15.0,
        listPricePer: 1_000_000,
        publishedUnit: "1M",
        region: "centralindia",
        source: { kind: "azure_retail_prices_api", query: RETAIL, productName: "Azure Speech", skuName: "S1", meterName: "S1 Neural Text To Speech Characters" },
        effectiveFrom: "2024-02-01",
        retrievedOn: "2026-09-23",
    },
    {
        // STS token issuance (sts/v1.0/issueToken). There is no meter for it in the price list: it
        // is an auth exchange, not a billed operation. Recorded at $0 so the mint still appears.
        id: "azure_speech:sts:unmetered",
        provider: "azure_speech",
        service: "sts",
        unit: "request",
        listPriceUsd: 0,
        listPricePer: 1,
        publishedUnit: "request",
        region: "centralindia",
        source: { kind: "azure_retail_prices_api", query: RETAIL, productName: "Azure Speech", skuName: "-", meterName: "(no meter — not billed)" },
        effectiveFrom: "2026-09-23",
        retrievedOn: "2026-09-23",
    },
];
/**
 * Admin price-book overrides (ai_models, migration 0092) — same contract as modelCost.ts's token
 * overrides: seeded with the entries above, authoritative once loaded, identical until edited.
 */
const meteredOverrides = new Map();
export function installMeteredPriceOverrides(entries) {
    meteredOverrides.clear();
    for (const e of entries)
        meteredOverrides.set(`${e.provider}:${e.service}`, e);
}
/** The entry priced for (provider, service), or null when neither the admin book nor the code has one. */
export function priceBookEntry(provider, service) {
    const base = AI_PRICE_BOOK.find((e) => e.provider === provider && e.service === service) ?? null;
    const o = meteredOverrides.get(`${provider}:${service}`);
    if (!o)
        return base;
    return {
        id: o.ref,
        provider: "azure_speech",
        service,
        unit: o.unit,
        listPriceUsd: o.unitPriceUsd,
        listPricePer: 1,
        publishedUnit: o.unit,
        region: base?.region ?? "",
        source: { kind: "admin_price_book", query: "ai_models", productName: "", skuName: "", meterName: base?.source.meterName ?? "" },
        effectiveFrom: base?.effectiveFrom ?? "",
        retrievedOn: base?.retrievedOn ?? "",
    };
}
/** USD per ONE unit (per audio second, per character, per request). */
export function unitPriceUsd(e) {
    return e.listPriceUsd / e.listPricePer;
}
/**
 * Billable characters of one neural TTS request, per Microsoft's published rule: every character
 * of the SSML body — text, punctuation, whitespace AND markup — EXCEPT the `<speak>` and `<voice>`
 * tags; one per Unicode code point, and each Han (Chinese/kanji/hanja) character counts two.
 * So `<prosody …>` and entity escapes such as `&amp;` are billed as written.
 */
export function ttsBillableCharacters(ssml) {
    const body = ssml.replace(/<\/?(speak|voice)\b[^>]*>/gi, "");
    let n = 0;
    for (const ch of body)
        n += /\p{Script=Han}/u.test(ch) ? 2 : 1;
    return n;
}
//# sourceMappingURL=aiPriceBook.js.map